/**
 * Error Boundary Component for React
 * Catches JavaScript errors anywhere in the component tree
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import './ErrorBoundary.css'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // eslint-disable-next-line no-console -- Error boundaries require logging for debugging
    console.error('Error Boundary caught an error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="error-boundary-fallback">
          <div className="error-content">
            <h1>Algo deu errado</h1>
            <p>
              Desculpe, ocorreu um erro inesperado. Nossa equipe foi notificada
              automaticamente.
            </p>

            <details className="error-details">
              <summary>Detalhes técnicos</summary>
              <pre>{this.state.error?.toString()}</pre>
            </details>

            <div className="error-actions">
              <button
                onClick={this.handleReset}
                className="btn btn-primary"
                type="button"
              >
                Tentar novamente
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-secondary"
                type="button"
              >
                Recarregar página
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
